import "./index.css";

import { useCallback, useMemo, useState } from "react";

import { useDocumentViewer } from "~/shared/components/document-viewer-flyout/hooks/use-document-viewer.ts";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useGridState } from "~/shared/hooks/use-grid-state.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { Invoice } from "~/shared/schemas/invoice-schema";
import { getDefaultPaymentGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { useGetUserPreferences } from "../profile/hooks/use-get-user-preferences.ts";
import { useGridPreferencesSync } from "../profile/hooks/use-grid-preferences-sync.ts";
import type { ConfirmationData } from "./confirmation-modal/index.tsx";
import { PaymentConfirmationModal } from "./confirmation-modal/index.tsx";
import { useCSVExport } from "./hooks/use-csv-export.ts";
import { useGetBraintreeToken } from "./hooks/use-get-braintree-token.ts";
import { useGetCustomerAccounts } from "./hooks/use-get-customer-accounts.ts";
import { useFetchInvoices } from "./hooks/use-get-invoices.ts";
import { useHandlePayment } from "./hooks/use-handle-payment.ts";
import { useInvoiceSelection } from "./hooks/use-invoice-selection.ts";
import { usePaymentColumns } from "./hooks/use-payment-columns.ts";
import { usePaymentModal } from "./hooks/use-payment-modal.ts";
import { usePaymentState } from "./hooks/use-payment-state.ts";
import { InvoiceControls } from "./invoice-controls/index.tsx";
import { PaymentModal } from "./modal/index.tsx";
import { PaymentActions } from "./payment-actions/index.tsx";
import { PaymentDocumentViewer } from "./payment-document-viewer/index.tsx";
import { PaymentGrid } from "./payment-grid/index.tsx";
import { PaymentHeader } from "./payment-header/index.tsx";
import { PAYMENT_EXPORT_COLUMNS } from "./utils/get-payment-columns.tsx";
import { isInvoiceEligibleForPayment } from "./utils/is-eligible-for-payment.ts";

export const Payment = () => {
	const { isInternal } = useDeploymentType();

	// State management
	const {
		currentStatus,
		currentFilter,
		selectedPaymentValue,
		handleStatusChange,
		handleFilterChange,
		handleApplyPaymentMethod,
		resetPaymentValue,
	} = usePaymentState();

	const {
		skip,
		take,
		handlePageChange,
		isLoadingPreference: isLoadingPageSize,
	} = useGridState(GridIdentifiers.Payment);

	// Data fetching
	// `isFetching` is true for both the initial load AND any background
	// refetch (e.g. after a successful payment invalidates the cache). We
	// pass it as the grid's loading flag so the skeleton rows appear while
	// the just-paid invoice statuses are refreshing.
	const {
		invoices = [],
		isLoading: isLoadingInvoices,
		isFetching: isFetchingInvoices,
	} = useFetchInvoices(currentStatus, currentFilter);
	const { isLoading: isLoadingToken } = useGetBraintreeToken();
	const { isLoading: isLoadingAccounts } = useGetCustomerAccounts();

	// Invoice selection
	const {
		selectedInvoices,
		selectedInvoiceByAction,
		handleSelect,
		handleDeselect,
		handleSelectAll,
		removeInvoice,
		clearSelection,
		isAllSelected,
	} = useInvoiceSelection(invoices);

	// Actions and utilities
	const { exportToCSV } = useCSVExport();
	const { modalState, closeModal, openLumpSumModal, openSelectedModal } =
		usePaymentModal();
	const { viewerState, openViewer, closeViewer, selectDocument } =
		useDocumentViewer();
	const { showWarning } = useNotificationActions();
	const [confirmationData, setConfirmationData] =
		useState<ConfirmationData | null>(null);
	const [showIneligibleNotice, setShowIneligibleNotice] = useState(false);

	// Server-persisted column layout (visibility/order/width) shared across the
	// Open/Pending Payment/Paid views via grid id 13. Mirrors the Shipment
	// Board: persistence is external-only (the GET is disabled and the PUT
	// short-circuits for internal users, who still get the reorder/resize UI).
	const { data: userPreferences, isLoading: isLoadingPreferences } =
		useGetUserPreferences(GridIdentifiers.Payment);
	// Kendo locks in column widths when the grid first mounts and ignores
	// later `width` prop changes. The preferences query resolves
	// asynchronously, so if the grid mounts before it returns the persisted
	// widths never apply. Gate a one-time remount on the query settling (the
	// query is disabled for internal users, so this is `true` immediately for
	// them) so the grid mounts fresh with the saved widths/order.
	const arePreferencesReady = !isLoadingPreferences;
	const {
		gridKey,
		onColumnVisibilityChange,
		onColumnReorder,
		onColumnResize,
		resetColumns,
	} = useGridPreferencesSync(GridIdentifiers.Payment, {
		defaultPrefs:
			getDefaultPaymentGridPrefs(isInternal)[GridIdentifiers.Payment]?.columns,
	});

	const { handleExecutePayment, isSubmitting } = useHandlePayment({
		selectedInvoices,
		selectedPaymentValue,
		clearSelection,
		closeModal,
		setConfirmationData,
	});

	// Event handlers
	const handleDocumentClick = useCallback(() => {
		openViewer();
	}, [openViewer]);

	// Mobile cards have no per-column cell renderer to hang this on, so the
	// same select-then-open wiring the grid's docs column uses is re-created
	// here for `PaymentGridMobile`.
	const handleCardDocumentClick = useCallback(
		(invoice: Invoice) => {
			handleSelect(invoice.invoiceID, true);
			handleDocumentClick();
		},
		[handleSelect, handleDocumentClick],
	);

	const selectRecord = useMemo(() => {
		const record: Record<string | number, boolean> = {};
		for (const item of selectedInvoices) {
			record[item.invoiceID] = true;
		}
		return record;
	}, [selectedInvoices]);

	// Resolve the displayed columns (visibility/order/width) against the user's
	// saved server preferences. `allColumns` feeds the column chooser; the
	// grid and CSV export use the visible subset.
	const { allColumns, visibleColumns } = usePaymentColumns({
		handleDeselect,
		handleSelect,
		selectRecord,
		isAllSelected,
		handleSelectAll,
		handleDocumentClick,
		isInternal,
		columnPrefs: userPreferences?.columns,
	});

	const visibleExportColumns = useMemo(() => {
		const visibleFields = new Set(visibleColumns.map((col) => col.field));
		return PAYMENT_EXPORT_COLUMNS.filter((col) => visibleFields.has(col.field));
	}, [visibleColumns]);

	const handleCloseViewer = useCallback(() => {
		closeViewer();
		clearSelection();
	}, [closeViewer, clearSelection]);

	const handleExportCSV = useCallback(() => {
		exportToCSV(selectedInvoices, visibleExportColumns);
	}, [exportToCSV, selectedInvoices, visibleExportColumns]);

	const handleResetSelectedPaymentValue = useCallback(() => {
		resetPaymentValue();
		closeModal();
		setShowIneligibleNotice(false);
	}, [resetPaymentValue, closeModal]);

	const handlePaySelected = useCallback(() => {
		const ineligible = selectedInvoices.filter(
			(invoice) => !isInvoiceEligibleForPayment(invoice),
		);
		const eligibleCount = selectedInvoices.length - ineligible.length;

		if (eligibleCount === 0) {
			// All selected invoices are ineligible — clear them from the
			// selection and surface a warning instead of opening an empty
			// payment modal that would submit a $0 payload.
			for (const invoice of ineligible) {
				removeInvoice(invoice.invoiceID);
			}
			setShowIneligibleNotice(false);
			showWarning(
				"None of the selected invoices are eligible for payment. Paid or Pending Payment invoices cannot be paid at this time.",
			);
			return;
		}

		if (ineligible.length > 0) {
			for (const invoice of ineligible) {
				removeInvoice(invoice.invoiceID);
			}
			setShowIneligibleNotice(true);
		} else {
			setShowIneligibleNotice(false);
		}
		openSelectedModal();
	}, [selectedInvoices, removeInvoice, openSelectedModal, showWarning]);

	const handleDismissIneligibleNotice = useCallback(() => {
		setShowIneligibleNotice(false);
	}, []);

	if (isLoadingToken || isLoadingAccounts) {
		return <LoadingSpinner />;
	}

	return (
		<TraxPageLayout>
			<TraxPageHeader showSupportMessage={!isInternal} title={"Invoices"} />
			<div className={"payment-container"}>
				{Boolean(modalState.show) && (
					<PaymentModal
						handleApplyPaymentMethod={handleApplyPaymentMethod}
						isSubmitting={isSubmitting}
						onClose={handleResetSelectedPaymentValue}
						onDismissIneligibleNotice={handleDismissIneligibleNotice}
						onExecutePayment={handleExecutePayment}
						onRemoveInvoice={removeInvoice}
						select={selectedInvoices}
						selectedPaymentValue={selectedPaymentValue}
						showIneligibleNotice={showIneligibleNotice}
						title={modalState.title}
					/>
				)}

				<PaymentHeader
					onFilterChange={handleFilterChange}
					selectedFilter={currentFilter}
				/>

				<div className={"payment-toolbar"}>
					<PaymentActions
						onExportCSV={handleExportCSV}
						onPayLumpSum={openLumpSumModal}
						onPaySelected={handlePaySelected}
						selectedCount={selectedInvoices.length}
					/>
					<InvoiceControls
						currentStatus={currentStatus}
						onFilterChange={handleStatusChange}
					/>
				</div>

				<PaymentGrid
					allColumns={allColumns}
					arePreferencesReady={arePreferencesReady}
					gridKey={gridKey}
					invoices={invoices}
					isAllSelected={isAllSelected}
					isLoading={
						isLoadingInvoices ||
						isFetchingInvoices ||
						(!isInternal && isLoadingPreferences) ||
						isLoadingPageSize
					}
					onColumnReorder={onColumnReorder}
					onColumnResize={onColumnResize}
					onColumnVisibilityChange={onColumnVisibilityChange}
					onDeselect={handleDeselect}
					onDocumentClick={handleCardDocumentClick}
					onPageChange={handlePageChange}
					onSelect={handleSelect}
					onSelectAll={handleSelectAll}
					resetColumns={resetColumns}
					selectRecord={selectRecord}
					skip={skip}
					take={take}
					visibleColumns={visibleColumns}
				/>
			</div>

			<PaymentDocumentViewer
				onClose={handleCloseViewer}
				onSelectDocument={selectDocument}
				selectedInvoice={selectedInvoiceByAction}
				viewerState={viewerState}
			/>
			{confirmationData !== null && (
				<PaymentConfirmationModal
					confirmationData={confirmationData}
					onClose={() => setConfirmationData(null)}
				/>
			)}
		</TraxPageLayout>
	);
};
